<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import { getUser } from '../lib/auth.js';
  import TextField from '../components/TextField.svelte';
  import Select from '../components/Select.svelte';
  import { ui } from '../lib/ui.svelte.js';

  let users = $state([]);
  let loading = $state(true);
  let err = $state('');
  let creating = $state(false);
  let newUsername = $state('');
  let newRole = $state('user');
  let lookupStatus = $state(''); // '', 'looking', 'found', 'missing', 'error'
  let lookupHint = $state('');   // human-readable note shown under the username field
  let lookupSeq = 0;             // ignore stale responses if user keeps typing
  let me = $state(null);

  const isAdmin = $derived(me?.portal_role === 'admin');

  const roleOptions = [
    { value: 'user',  label: 'User',  supporting: 'create webhooks, view captures' },
    { value: 'admin', label: 'Admin', supporting: 'manage users + everything below' },
  ];
  const denseRoleOptions = [
    { value: 'user',  label: 'User'  },
    { value: 'admin', label: 'Admin' },
  ];

  async function load() {
    loading = true; err = '';
    try { users = (await api.listUsers()).users || []; }
    catch (e) { err = e.message; }
    finally { loading = false; }
  }

  onMount(() => { me = getUser(); load(); });

  async function grant() {
    if (!newUsername.trim()) return;
    try {
      // Name/email/mobile aren't sent -- the server resolves them from
      // enswitch and keeps them synced. We only choose username + role.
      await api.grantUser({ username: newUsername, role: newRole });
      newUsername = ''; newRole = 'user';
      lookupStatus = ''; lookupHint = '';
      creating = false;
      ui.snack('Access granted', 'success');
      load();
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  async function lookupUsername() {
    const u = newUsername.trim();
    if (!u) { lookupStatus = ''; lookupHint = ''; return; }
    const seq = ++lookupSeq;
    lookupStatus = 'looking';
    lookupHint = 'Looking up directory...';
    try {
      const dir = await api.lookupDirectoryUser(u);
      if (seq !== lookupSeq) return; // newer lookup superseded this one
      lookupStatus = 'found';
      const name = [dir.first_name, dir.last_name].filter(Boolean).join(' ');
      lookupHint = `Matched #${dir.user_id}${name ? ' — ' + name : ''}${dir.email ? ' (' + dir.email + ')' : ''}`;
    } catch (e) {
      if (seq !== lookupSeq) return;
      if (e.message.includes('directory not available')) {
        lookupStatus = 'error';
        lookupHint = 'Directory unavailable; grant will be created as pending.';
      } else {
        lookupStatus = 'missing';
        lookupHint = 'Not in directory; grant will be created as pending and reconciled on first login.';
      }
    }
  }

  async function revoke(u) {
    const who = u.username || u.email || '#'+u.user_id;
    const ok = await ui.ask({
      title: 'Revoke access?',
      message: `${who} will lose access to the portal. They can be re-added later.`,
      confirmText: 'Revoke',
      destructive: true,
    });
    if (!ok) return;
    try { await api.revokeUser(u); ui.snack('Access revoked', 'success'); load(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  async function changeRole(u, role) {
    if (u.role === role) return;
    try {
      await api.setUserRole(u, role);
      ui.snack(`Role updated to ${role}`, 'success');
      load();
    } catch (e) { ui.snack(e.message, 'error'); load(); }
  }

  // rowKey is stable across the user_id<=0 (pending) case so per-row state
  // (e.g. which invite is sending) keys correctly.
  function rowKey(u) { return u.user_id > 0 ? String(u.user_id) : 'u:' + u.username; }

  // A grant is "pending" until the user logs in for the first time -- the
  // placeholder _id is negative. Those are the rows worth nudging with an
  // invite email.
  function isPending(u) { return u.user_id <= 0; }
  let inviting = $state(null); // rowKey currently sending, for button state
  async function invite(u) {
    inviting = rowKey(u);
    try {
      const r = await api.inviteUser(u);
      ui.snack(`Invite sent to ${r.to}`, 'success');
    } catch (e) { ui.snack(e.message, 'error'); }
    finally { inviting = null; }
  }

  function fmt(t) { return t ? new Date(t).toLocaleString() : '—'; }
</script>

{#if isAdmin}
  <div class="flex justify-end mb-4">
    <button onclick={() => (creating = !creating)} class="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary text-on-primary text-sm font-medium px-3 sm:px-5 py-2.5 sm:py-2" title="Grant access">
      <span class="material-symbols-outlined text-base">person_add</span>
      <span class="hidden sm:inline">Grant access</span>
    </button>
  </div>
{/if}

{#if !isAdmin}
  <p class="mb-4 text-sm text-on-surface-variant">
    You have <span class="font-medium text-on-surface">user</span> access. Only admins can grant or revoke access.
  </p>
{/if}

{#if creating && isAdmin}
  <section class="bg-surface-container rounded-2xl p-4 mb-4 grid md:grid-cols-3 gap-4">
    <div class="md:col-span-2">
      <TextField
        label="Username *"
        bind:value={newUsername}
        leadingIcon="badge"
        onblur={lookupUsername}
      />
      {#if lookupHint}
        <p class="mt-1 text-xs"
           class:text-primary={lookupStatus === 'found'}
           class:text-error={lookupStatus === 'error'}
           class:text-on-surface-variant={lookupStatus !== 'found' && lookupStatus !== 'error'}>
          {lookupHint}
        </p>
      {/if}
    </div>
    <Select    label="Role"        bind:value={newRole} options={roleOptions} />
    <div class="md:col-span-3 text-sm text-on-surface-variant -mt-1">
      The Telebroad username -- typically the user's email, but can also be a
      short handle (e.g. <span class="font-mono">jmertz</span>). Usernames are
      unique in the directory; name, email and mobile are pulled from enswitch
      automatically and kept in sync on each login (read-only here). If the
      username isn't found yet, the grant is created as pending and reconciled
      on first login.
    </div>
    <div class="md:col-span-3 flex justify-end gap-2">
      <button onclick={() => (creating = false)} class="px-4 py-2 rounded-full text-sm text-on-surface-variant hover:bg-surface-container-high">Cancel</button>
      <button onclick={grant} class="px-6 py-2.5 rounded-full bg-primary text-on-primary text-sm font-medium">Grant</button>
    </div>
  </section>
{/if}

{#if loading}
  <p class="text-on-surface-variant">Loading...</p>
{:else if err}
  <p class="text-error">{err}</p>
{:else if users.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">group</span>
    <p>No users have access yet.</p>
  </div>
{:else}
  <div class="bg-surface-container rounded-2xl overflow-x-auto">
    <table class="w-full text-sm min-w-[720px]">
      <thead class="text-on-surface-variant text-xs uppercase tracking-wide">
        <tr class="border-b border-outline-variant">
          <th class="text-left px-4 py-3">User ID</th>
          <th class="text-left px-4 py-3">Username</th>
          <th class="text-left px-4 py-3">Name</th>
          <th class="text-left px-4 py-3">Email</th>
          <th class="text-left px-4 py-3">Mobile</th>
          <th class="text-left px-4 py-3">Role</th>
          <th class="text-left px-4 py-3">Last seen</th>
          <th class="text-left px-4 py-3">Granted by</th>
          <th class="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {#each users as u (u.user_id)}
          <tr class="border-b border-outline-variant/50 hover:bg-surface-container-high">
            <td class="px-4 py-3 font-mono text-xs whitespace-nowrap">
              {u.user_id < 0 ? '(pending login)' : u.user_id}
            </td>
            <td class="px-4 py-3 whitespace-nowrap">{u.username || '—'}</td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">
              {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
            </td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">{u.email || '—'}</td>
            <td class="px-4 py-3 text-on-surface-variant font-mono text-xs whitespace-nowrap">{u.mobile || '—'}</td>
            <td class="px-4 py-3 whitespace-nowrap">
              {#if isAdmin && me?.user_id !== u.user_id}
                <Select
                  dense
                  value={u.role || 'user'}
                  options={denseRoleOptions}
                  onchange={(e) => changeRole(u, e.target.value)}
                />
              {:else}
                <span class="px-2 py-0.5 rounded-md text-xs font-medium {u.role === 'admin' ? 'bg-primary/15 text-primary' : 'bg-surface-variant text-on-surface-variant'}">
                  {u.role || 'user'}
                </span>
              {/if}
            </td>
            <td class="px-4 py-3 text-on-surface-variant whitespace-nowrap">{fmt(u.last_seen_at)}</td>
            <td class="px-4 py-3 text-on-surface-variant text-xs whitespace-nowrap">
              {u.granted_by || (u.granted_by_id ? '#'+u.granted_by_id : 'bootstrap')}
            </td>
            <td class="px-4 py-3 text-right whitespace-nowrap">
              {#if me && u.user_id === me.user_id}
                <span class="text-xs text-on-surface-variant">you</span>
              {:else if isAdmin}
                <div class="user-actions">
                  {#if isPending(u)}
                    <button
                      onclick={() => invite(u)}
                      disabled={inviting === rowKey(u)}
                      class="user-invite-btn"
                      title="Email a sign-in link"
                    >
                      <span class="material-symbols-outlined text-sm">mail</span>
                      {inviting === rowKey(u) ? 'Sending…' : 'Invite'}
                    </button>
                  {/if}
                  <button onclick={() => revoke(u)} class="text-on-surface-variant hover:text-error" title="Revoke access">
                    <span class="material-symbols-outlined text-base">person_remove</span>
                  </button>
                </div>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .user-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: flex-end;
  }
  .user-invite-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 999px;
    background: transparent;
    color: rgb(var(--md-sys-color-primary));
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }
  .user-invite-btn:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
  .user-invite-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
